// © Microsoft Corporation. All rights reserved.

import { AccountType, AppType, AudienceModeType, AuthCookieName } from "@msnews/experiences-constants";
import { EnterpriseOneServiceApi, appAnonCookieExistsCookieName, childCookieName, rootElementId, serviceUrl, telemetryId, trackPageProduct, trackPageType } from "../constants";
import { Environment, getEnvironmentFromString } from "./Environment";
import { HostPage, IHostPage } from "./HostPage";
import { canUseDOM, getDocumentElementById, getLocationHostname, getLocationHref, getLocationOrigin, getLocationSearch } from "@msnews/isomorphic-util";
import { getCookie, getMuidCookie } from "../utilities/CookieUtility";

import { AccountInfo } from "./Enterprise";
import { Market } from "./Market";
import { PageRequestTargetScope } from "./PageRequestTargetScope";
import { PlatformType } from "cms-models-msn/configuration/types/PlatformType";
import { UrlUtility } from "../utilities/UrlUtility";
import { Utility } from "../utilities/Utility";
import { headData } from "./HeadData";
import { isString } from "lodash-es";
import { logger } from "@msnews/diagnostics";

/**
 * chrome
 * The chrome API is not defined in Typescript
 */
declare global {
    // tslint:disable-next-line: completed-docs
    interface Window { chrome: any; }
}

/**
 * Page level experience tracking info
 */
export interface ITrackInfo {
    sitePage: {
        page_product: string,
        page_type: string
    }
}

export class AppEnvironment {
    /**
     * The single instance of the AppEnvironment class.
     * @type {AppEnvironment}
     */
    private static _instance: AppEnvironment;

    /**
     * the activity id for the current page view
     */
    private activityId: string;

    /**
     * Base url for the content service, endpoints/queries are appended for specific content calls.
     * This is an akamai url for highly cacheable content.
     * Possible values are enumerated on ContentServiceConfig.ts
     *
     * @private
     * @type {string}
     * @memberof AppEnvironment
     */
    private contentServiceUrlBase: URL;

    private currentRequestTargetScope: PageRequestTargetScope;

    private currentMarket: Market;
    private hostPage: IHostPage;

    /** The hostname to use for content links. */
    private currentContentLinkHost: string;

    /**
     * Indicates that the page is running as an FRE page (i.e. the url contains fre=1)
     * Does not indicate that the FRE itself is currently displayed over the page.
     */
    private isFrePage: boolean;

    /**
     * Base for the service url, endpoints/queries are appended for specific service calls.
     * This is the OneService url used for personalized content.
     * Possible values are enumerated on OneServiceConfig.ts
     *
     * @private
     * @type {string}
     * @memberof AppEnvironment
     */
    private serviceUrlBase: URL;

    /**
     * Base URL for the feed service
     */
    private feedServiceUrlBase: URL;

    /**
     * The account type of the page user
     * Currently this is only implemented for the Anaheim New Tab Pages
     */
    private accountType: AccountType;

    /**
     * The account info for the current page user wrapped in a promise
     */
    private accountInfoPromise: Promise<AccountInfo>;

    private appType: string;
    private authCookieName: AuthCookieName;
    private buildVersion: string;

    private environmentConfigUrl: URL;

    private rootTelemetryId: number;
    private trackInfo: ITrackInfo;
    private userId: string;
    private countryCode: string;
    private latitude: string;
    private longitude: string;
    private environment: Environment;
    private assetsHost: string;
    private featureFlags: any;
    private disableCachingConfigs: boolean;
    private enableConfigService: boolean;
    private isMultiConfigRetrievalEnabled: boolean;
    private configServiceBaseUrl: URL;
    private disableContentPreviewCache: boolean;
    private disableContentCacheIDBRead: boolean;
    private disableContentListCache: boolean;
    private disableContentListCacheIDBRead: boolean;
    private sendFeedCallActivityIdInHeader: boolean;

    /** True if query string has appropriate debug flag */
    private isDebug: boolean;

    /** True if query string has appropriate flag to show msnCreatorDashboard (msnDash=<any>) */
    private showMnsCreatorDashboard: boolean;

    /** Config Root Url */
    private configRootUrl: URL;

    /** Cache busting identifier received from pcs */
    private cacheBustingIdentifier: string;

    /**
     * ActivityId value from initial server response, use telemetry's requestId instead for associating with current page client navigation
     */
    public get ActivityId(): string { return this.activityId; }

    /**
     * Getter for the base url for the content service, endpoints/queries are appended for specific content calls.
     * This is an akamai url for highly cacheable content.
     * Possible values are enumerated on ContentServiceConfig.ts
     *
     * @readonly
     * @type {URL}
     * @memberof AppEnvironment
     */
    public get EnvironmentConfigUrl(): URL {
        // If I mutate the same object, I will need to revert to the original state somewhere.
        // For safety reason, it is better to use a new Object
        const url = new URL("", this.environmentConfigUrl);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.config", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.config");
        if (searchParam) {
            url.searchParams.set("env.config", searchParam);
        }

        return url;
    }

    /**
     * Getter for the base url for the content service, endpoints/queries are appended for specific content calls.
     * This is an akamai url for highly cacheable content.
     * Possible values are enumerated on ContentServiceConfig.ts
     *
     * @readonly
     * @type {URL}
     * @memberof AppEnvironment
     */
    public get ContentServiceUrlBase(): URL {
        // If I mutate the same object, I will need to revert to the original state somewhere.
        // For safety reason, it is better to use a new Object
        const url = new URL("", this.contentServiceUrlBase);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.content", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.content");
        if (searchParam) {
            url.searchParams.set("env.content", searchParam);
        }

        return url;
    }

    public get CurrentRequestTargetScope(): PageRequestTargetScope { return this.currentRequestTargetScope; }
    public get CurrentMarket(): Market { return this.currentMarket; }
    public get Environment(): Environment { return this.environment; }
    public get AssetsHost(): string { return this.assetsHost; }
    public get HostPage(): IHostPage { return this.hostPage; }
    public get AppType(): string { return this.appType; }
    public get AuthCookieName(): AuthCookieName { return this.authCookieName; }
    public get AccountType(): AccountType { return this.accountType; }
    public get AccountInfoPromise(): Promise<AccountInfo> { return this.accountInfoPromise; }
    public get BuildVersion(): string { return this.buildVersion; }

    /** True if query string has appropriate debug flag */
    public get IsDebug(): boolean { return this.isDebug; }

    /** True if query string has appropriate flag to show msnCreatorDashboard (msnDash=<any>) */
    public get ShowMnsCreatorDashboard(): boolean { return this.showMnsCreatorDashboard; }

    /**
     * Gets the host to use when linking to content.
     */
    public get contentLinkHost(): string { return this.currentContentLinkHost; }

    /**
     * Getter for the base for the service url, endpoints/queries are appended for specific service calls.
     * This is the oneservice url used for personalized content.
     * Possible values are enumerated on OneServiceConfig.ts
     *
     * @type {URL}
     * @memberof AppEnvironment
     */
    public get ServiceUrlBase(): URL {
        const url = new URL("", this.serviceUrlBase);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }

        return url;
    }

    /**
     * Base URL for the feed service
     */
    public get FeedServiceUrlBase(): URL {
        if (!this.feedServiceUrlBase) {
            return null;
        }

        const url = new URL("", this.feedServiceUrlBase);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }

        return url;
    }

    /**
     * Base URL for the enterprise service
     */
    public get EnterpriseServiceUrlBase(): URL {
        const url = new URL("", EnterpriseOneServiceApi);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }

        return url;
    }

    public get IsFrePage(): boolean { return this.isFrePage; }

    public get RootTelemetryId(): number { return this.rootTelemetryId; }
    public get TrackInfo(): ITrackInfo { return this.trackInfo; }
    public get UserId(): string { return this.getUserMuid() || this.userId; }

    /**
     * This is the switch to turn on/off indexed db operation used as a caching layer in config resolver (Resolver.ts)
     */
    public get DisableCachingConfigs(): boolean { return this.disableCachingConfigs; }

    /**
     * This is the switch to turn on/off config service operation in Resolver.ts
     */
    public get EnableConfigService(): boolean { return this.enableConfigService; }

    /**
     * This indicates whether we will use CMS PPE configs or not
     */
    public get EnableCMSPPEConfigs(): boolean { return headData && headData.EnableCMSPPEConfigs; }

    /**
     * Check whether multi config retrieval is supported.
     */
    public get IsMultiConfigRetrievalEnabled(): boolean { return this.isMultiConfigRetrievalEnabled; }

    /**
     * This is the switch to turn on/off content preview caching in core/cache/ContentPreview.ts
     */
    public get DisableContentPreviewCache(): boolean {
        return this.disableContentPreviewCache;
    }

    /**
     * Returns a flag indicating whether the one service feed fetch call should send activityID
     * in the request header or as a QSP in the URL (current behavior is via QSP).
     */
    public get SendFeedCallActivityIdInHeader(): boolean {
        return this.sendFeedCallActivityIdInHeader;
    }

    /**
     * This is the switch to turn off hydrating content preview cache from IndexedDB.
     * Default is false
     */
    public get DisableContentCacheIDBRead(): boolean {
        const queryParamOverride = UrlUtility.getQueryParameterByName("disableContentCacheIDBRead", getLocationSearch());

        if (queryParamOverride) {
            return queryParamOverride === "true";
        }

        return this.disableContentCacheIDBRead;
    }

    /**
     * This is the switch to turn on/off content list caching in core/cache/ContentPreview.ts
     */
    public get DisableContentListCache(): boolean {
        return this.disableContentListCache;
    }

    /**
     * This is the switch to turn off hydrating content list cache from IndexedDB.
     * Default is false
     */
    public get DisableContentListCacheIDBRead(): boolean {
        const queryParamOverride = UrlUtility.getQueryParameterByName("disableContentListCacheIDBRead", getLocationSearch());

        if (queryParamOverride) {
            return queryParamOverride === "true";
        }

        return this.disableContentListCacheIDBRead;
    }

    /**
     * Get base url of Config Service.
     */
    public get ConfigServiceBaseUrl(): URL { return this.configServiceBaseUrl; }

    /**
     * This returns feature flags client settings stamped by PCS.
     */
    public get FeatureFlags(): any { return this.featureFlags; }

    /**
     * This returns config root url
     */
    public get ConfigRootUrl(): URL {
        if (!this.configRootUrl) {
            return null;
        }

        const url = new URL("", this.configRootUrl);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.config", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.config");
        if (searchParam) {
            url.searchParams.set("env.config", searchParam);
        }

        return url;
    }

    /**
     * Returns cache busting identifier from PCS.
     */
    public get CacheBustingIdentifier(): string { return this.cacheBustingIdentifier; }

    /**
     * Returns the country code. For example "br"
     */
    public get CountryCode(): string { return this.countryCode; }

    //// FIXME: hyphen has a second 'h'
    //// Bug 24228723: [Peregrine] ActivityIdLowerCaseNoHypens misspells "Hyphen"
    /**
     * Gets the Activity ID in lower case and without any hyphens. This helper method should be where parity with Prime is required.
     * Use telemetry's requestId instead for associating with current page client navigation
     */
    public get ActivityIdLowerCaseNoHypens(): string { return ((headData && headData.ClientSettings && headData.ClientSettings.aid) || "").toLowerCase(); }

    /**
     * Returns the latitude. For example "42.361145"
     */
    public get Latitude(): string { return this.latitude; }

    /**
     * Returns the longitude. For example "-71.057083"
     */
    public get Longitude(): string { return this.longitude; }

    public set ActivityId(value: string) { this.activityId = value; }
    public set CountryCode(value: string) { this.countryCode = value; }
    public set CurrentMarket(value: Market) { this.currentMarket = value; }
    public set CurrentRequestTargetScope(value: PageRequestTargetScope) { this.currentRequestTargetScope = value; }
    public set Environment(value: Environment) { this.environment = value; }
    public set HostPage(value: IHostPage) { this.hostPage = value; }
    public set RootTelemetryId(value: number) { this.rootTelemetryId = value; }
    public set TrackInfo(value: ITrackInfo) { this.trackInfo = value; }
    public set UserId(value: string) { this.userId = value; }
    public set AuthCookieName(value: AuthCookieName) { this.authCookieName = value; }
    public set AccountType(value: AccountType) { this.accountType = value; }
    public set AccountInfoPromise(value: Promise<AccountInfo>) { this.accountInfoPromise = value; }

    /**
     * Initialize the AppEnvironment singleton instance.
     * @param _serviceUrl Optional URL to initialize the ServiceUrlBase.
     * @param _configServiceUrlBase ConfigServiceUrlBase provided during server side rendering this is the ServiceFabric ClusterRegionUrl.
     */
    private constructor(_serviceUrl: URL, _configServiceUrlBase: string) {
        this.initializeAppEnvironment(_serviceUrl, _configServiceUrlBase);
    }

    /**
     * Returns singleton class instance.
     * @returns {AppEnvironment}
     */
    public static getInstance(serviceUrl: URL = null, configServiceUrlBase: string = null): AppEnvironment {
        return AppEnvironment._instance || (AppEnvironment._instance = new AppEnvironment(serviceUrl, configServiceUrlBase));
    }

    /**
     * Reinitialize the singleton class instance.
     * @param serviceUrl OneService service url to initialize to.
     * @param configServiceUrlBase Config service url to initialize to.
     * @returns {AppEnvironment}
     */
    public static resetInstance(serviceUrl: URL = null, configServiceUrlBase: string = null): AppEnvironment {
       AppEnvironment._instance = new AppEnvironment(serviceUrl, configServiceUrlBase);
       appEnvironment = AppEnvironment._instance;
       return AppEnvironment._instance;
    }

    /**
     * Initializes App environment. This is normally only called by the constructor (singleton pattern)
     * It is made public so that it can be also called by test code.
     */
    public initializeAppEnvironment(_serviceUrl?: URL, configServiceUrlBase?: string): void {

        let environmentFromHeadData: Environment = null;
        const clientSettings = headData && headData.ClientSettings;

        if (clientSettings) {
            this.activityId = this.tryReformatGuid(clientSettings.aid);
            this.buildVersion = clientSettings.v;
            this.currentMarket = (headData.Locale || clientSettings.apps_locale) as Market;
            this.countryCode = clientSettings.geo_country;
            this.latitude = clientSettings.geo_lat;
            this.longitude = clientSettings.geo_long;
            this.configRootUrl = clientSettings.configRootUrl ? new URL("", clientSettings.configRootUrl) : null;
            environmentFromHeadData = getEnvironmentFromString(clientSettings.env);

            this.setAccountType();
            this.setAccountInfoPromise();
            this.appType = clientSettings.apptype || "";
            this.authCookieName = this.getAuthCookieName();

            this.featureFlags = clientSettings.featureFlags;

            this.disableCachingConfigs =
                this.featureFlags && this.featureFlags.disableCachingConfigs
                    ? this.featureFlags.disableCachingConfigs === "true"
                    : false;

            this.enableConfigService =
                this.featureFlags && this.featureFlags.enableConfigService
                    ? this.featureFlags.enableConfigService === "true"
                    : true;

            this.disableContentPreviewCache =
                this.featureFlags && this.featureFlags.disableContentPreviewCache
                    ? this.featureFlags.disableContentPreviewCache === "true"
                    : false;

            this.disableContentCacheIDBRead = this.featureFlags && this.featureFlags.disableContentCacheIDBRead
                ? this.featureFlags.disableContentCacheIDBRead === "true"
                : false;

            this.disableContentListCache =
                this.featureFlags && this.featureFlags.disableContentListCache
                    ? this.featureFlags.disableContentListCache === "true"
                    : false;

            this.disableContentListCacheIDBRead = this.featureFlags && this.featureFlags.disableContentListCacheIDBRead
                ? this.featureFlags.disableContentListCacheIDBRead === "true"
                : false;

            // Hardcode flight details for experimentation sake
            // Use feature flag as a switch to disable the feature, since we may not be able to stop the flight itself (will need a PCS deployment)
            this.sendFeedCallActivityIdInHeader = this.featureFlags && this.featureFlags.disableActivityIdInHeader && this.featureFlags.disableActivityIdInHeader === "true"
                ? false
                : this.appType === AppType.Edge;

            this.cacheBustingIdentifier = clientSettings.cbid;
        }

        const rootElement = canUseDOM() ? getDocumentElementById(rootElementId) : null;
        const locationHref = getLocationHref();
        const locationOrigin = getLocationOrigin();

        // env is read from
        //  - query string param env, if present
        //  - headData.ClientSettings.env
        //  - defaults to prod otherwise
        const envQSOverwrite = getEnvironmentFromString(UrlUtility.getQueryParameterByName("env", locationHref));
        this.environment =
            envQSOverwrite
            || environmentFromHeadData
            || Environment.prod;

        this.serviceUrlBase = _serviceUrl || (rootElement && rootElement.getAttribute(serviceUrl) && new URL("", rootElement.getAttribute(serviceUrl))) || new URL("", "https://assets.msn.com/service/");

        // If PCS sent the feed base domain, use it for the feed URL base
        // or else default to host page rooted URL.
        this.feedServiceUrlBase = new URL("./service/", clientSettings && clientSettings.feedBaseDomain ? clientSettings.feedBaseDomain : locationOrigin);

        // Get the hostPage from the request url
        this.hostPage = HostPage.getHostPageFromUrl(locationHref);

        // Determine if the page is running as an FRE page
        this.isFrePage = (UrlUtility.getQueryParameterByName("fre", locationHref) === "1");

        // Find the host for the Akamai assets store.
        this.assetsHost = getLocationHostname().endsWith(".cn") ? "assets.msn.cn" : "assets.msn.com";

        this.userId = this.getUserMuid();

        // Get attributes from the head (full page Peregrine experience) or from the spa "root" element (PrimeHybrid case)
        // TODO, determine where in the <head> element attributes these values should be set
        this.rootTelemetryId = rootElement ? Number(rootElement.getAttribute(telemetryId)) : 0;
        const trackPageProd = rootElement && rootElement.getAttribute(trackPageProduct);
        const trackPageTypeVal = rootElement && rootElement.getAttribute(trackPageType);
        this.trackInfo = { sitePage: { page_product: trackPageProd, page_type: trackPageTypeVal } };

        this.contentServiceUrlBase = new URL("", "https://assets.msn.com/content/");

        this.currentContentLinkHost = this.createContentLinkHost();

        this.setTargetScope();

        this.isMultiConfigRetrievalEnabled = this.useMultiConfigsRetrieval();

        const baseUrl = this.getConfigServiceBaseUrl(this.isMultiConfigRetrievalEnabled, configServiceUrlBase);
        this.configServiceBaseUrl = baseUrl ? new URL("", baseUrl) : null;

        this.environmentConfigUrl = new URL("", "https://assets.msn.com/config/v1/");

        this.isDebug = ((UrlUtility.getQueryParameterByName("debug", locationHref) || "").toLowerCase() in { "1": 1, "true": 1 })
            || (UrlUtility.getQueryParameterByName("item", locationHref) || "").toLowerCase().indexOf("/debug") > 0;

        this.showMnsCreatorDashboard = UrlUtility.getQueryParameterByName("mnsDash", locationHref) != null;
    }

    /**
     * Get the UserMuid from the muid cookie
     */
    private getUserMuid(): string {
        let muid: string;
        const muidCookie: string = getMuidCookie();
        if (isString(muidCookie)) {
            muid = "m-" + muidCookie;
        }
        return muid;
    }

    /**
     * Get config service base url
     * @param isMultiConfigRetrievalEnabled if multiple config retrieval is supported.
     * @param configServiceUrlBase externally configured config service url (from SSR environment)
     * @returns {string} - config service base url.
     */
    private getConfigServiceBaseUrl(isMultiConfigRetrievalEnabled: boolean = true, configServiceUrlBase?: string): string {
        const relativePath = `/resolver/api/resolve/${isMultiConfigRetrievalEnabled ? `v2/` : ``}configindex/`;

        if (configServiceUrlBase) {
            // When doing server side rendering the ClusterRegionUrl env variable should be set, use this to avoid calls to CRS going outside of the cluster.
            return configServiceUrlBase + relativePath;
        } else if (process.env.WEBPACK_DEV_SERVER || this.environment === Environment.int) {
            // int and localhost to use int.msn.com for config service.
            return "https://int.msn.com" + relativePath;
        } else {
            return getLocationOrigin() ? getLocationOrigin()  + relativePath : null;
        }
    }

    /**
     * Function checks if ConfigService should return multiple configs
     * @returns true if supported else false.
     */
    private useMultiConfigsRetrieval(): boolean {
        const urlQuery = getLocationSearch();
        if (urlQuery) {
            const param = UrlUtility.getQueryParameterByName("isMultiConfigRetrievalEnabled", urlQuery);
            if (param) {
                return param.toLocaleLowerCase() === "true";
            }
        }

        // PCS flag to turn on/off multi config retrieval.
        if (this.featureFlags
            && this.featureFlags.isMultiConfigRetrievalEnabled) {
            return this.featureFlags.isMultiConfigRetrievalEnabled === "true";
        }

        return true;
    }

    /**
     * Get the auth cookie name based on the app type of the page
     * @returns {AuthCookieName} - the name of the auth cookie to use
     */
    private getAuthCookieName(): AuthCookieName {
        const clientSettings = headData && headData.ClientSettings;
        const appType: AppType = (clientSettings.apptype || "") as AppType;
        let authCookieName: AuthCookieName;
        switch (appType) {
            case AppType.FirstParty:
            case AppType.Hub:
            case AppType.MicrosoftNews:
            case AppType.Views:
                authCookieName = AuthCookieName.Anon;
                break;
            case AppType.Edge:
            case AppType.EdgeChromium:
            case AppType.MMX:
            case AppType.Office:
            default:
                authCookieName = AuthCookieName.AppAnon;
        }

        return authCookieName;
    }

    /**
     * Sets the AccountType for the current user
     */
    private setAccountType(): void {
        // TODO: Currently signed in status is not available on the server side
        // Tracking work item: https://dev.azure.com/microsoft/OS/_workitems/edit/24489062
        if (!canUseDOM() || !window.chrome || !window.chrome.authPrivate || !window.chrome.authPrivate.getPrimaryAccountInfo) {
            this.AccountType = AccountType.SignedOut;
            return;
        }
        window.chrome.authPrivate.getPrimaryAccountInfo((accountInfo) => {
            if (!accountInfo) {
                this.AccountType = AccountType.SignedOut;
                return;
            }

            const accountType: string = accountInfo.account_type;
            if (accountType === "AAD") {
                this.AccountType = AccountType.AAD;
                return;
            }

            if (accountType === "MSA") {
                this.AccountType = AccountType.MSA;
                return;
            }

            this.AccountType = AccountType.SignedOut;
        });
    }

    /**
     * Sets the AccountInfoPromise for the current user
     */
    private setAccountInfoPromise(): void {
        this.AccountInfoPromise = new Promise((resolve) => {
            if (!canUseDOM() || !window.chrome || !window.chrome.authPrivate || !window.chrome.authPrivate.getPrimaryAccountInfo) {
                resolve(null);
            }

            window.chrome.authPrivate.getPrimaryAccountInfo((accountInfo) => {
                resolve(accountInfo);
            });
        })
    }

    /**
     * Determines the content link host to use.
     * @returns The content link host.
     */
    private createContentLinkHost(): string {
        const topLevelDomain = this.currentMarket === Market.ZHCN ? "cn" : "com";
        return "www.msn." + topLevelDomain;
    }

    /**
     * Reformats the given guid without dashes to a guid string with dashes.
     * @param {string} guid - The guid without dashes.
     * @returns {string} - The reformatted GUI with dashes.
     */
    private tryReformatGuid(guid: string): string {
        if (guid && guid.length === 32) {
            // use a regex to extract the specific substrings to reformat the guid
            const matches = guid.match(/(\w{8})(\w{4})(\w{4})(\w{4})(\w{12})/i);
            if (matches) {
                // join all the captured groups from 1-5 to format the final guid string
                guid = matches.slice(1).join("-");
            }
        }

        return guid;
    }

    /**
     * Sets target scope for current request.
     */
    private setTargetScope(): void {
        if (!headData || !headData.ClientSettings) {
            return;
        }

        // If user is signed in and "child" Cookie exists - set Kids audience mode, otherwise use PCS provided Audience mode.
        let audienceMode: AudienceModeType;
        if (getCookie(appAnonCookieExistsCookieName) && getCookie(childCookieName)) {
            audienceMode = AudienceModeType.Kids;
        } else {
            audienceMode = headData.ClientSettings.audienceMode || AudienceModeType.Adult;
        }

        const { browser, deviceFormFactor, domain, locale, os, pagetype } = headData.ClientSettings;

        this.currentRequestTargetScope = {
            audienceMode: audienceMode,
            browser: browser,
            deviceFormFactor: deviceFormFactor,
            domain: domain,
            locale: locale,
            os: os,
            platform: PlatformType.Web,
            pageType: pagetype
        };

        // if locale is not defined in client settings, try reading it from headData for backward compatibility
        if (!this.currentRequestTargetScope.locale && Utility.stringHasData(headData.Locale)) {
            const locale = headData.Locale.split("-");
            if (locale.length > 1) {
                this.currentRequestTargetScope.locale = {
                    language: locale[0],
                    // Some spartan locale are in format "ha-latn-ng". Here the market is "ng" and language is "ha"
                    market: locale[locale.length - 1]
                };
            }
        }

        if (Utility.stringHasData(headData.CurrentFlights)) {
            this.currentRequestTargetScope.pageExperiments = headData.CurrentFlights.split(",");
        }
    }
}

export const getAppEnvironment = AppEnvironment.getInstance;

// appEnvironment can be reinitialized for server side rendering through resetAppEnvironment.
export let appEnvironment = AppEnvironment.getInstance();

export const resetAppEnvironment = AppEnvironment.resetInstance;